import { FC, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@moment/hooks/useTheme.ts';
import RecirculationCard, {
  RecirculationCardProps,
} from '../RecirculationCard/RecirculationCard';
import RecirculationCardSkeleton from '../RecirculationCardSkeleton/RecirculationCardSkeleton';
import { generateFetchLookbookRecirculationQuery } from '../../net/gallery-fetch-query';
import { LightboxItem } from '../../net/softbox-types';
import './RecirculationPage.scss';
import { CommonPageDataContext } from '@pages/Root/ContextWrapper/WithInterest/CommonPageDataContext.ts';
import { getSSRTranslation } from '@/i18n.ts';

interface RecirculationPageProps {
  onScroll?: (isScrolledDown: boolean) => void;
  lookbookId?: string;
  enabled?: boolean;
}

const RecirculationPage: FC<RecirculationPageProps> = ({
  onScroll,
  lookbookId,
  enabled = true,
}) => {
  const { i18n, t } = useTranslation();
  const { interests } = useContext(CommonPageDataContext);
  const { theme } = useTheme();
  const { data, isLoading } = useQuery({
    queryKey: ['recirculationlookbook', lookbookId, i18n.language],
    enabled,
    queryFn: generateFetchLookbookRecirculationQuery({
      lookbookId: lookbookId,
      lang: i18n.language,
    }),
  });

  const cards: RecirculationCardProps[] =
    (data?.items ?? []).map((item: LightboxItem) => {
      const i = interests.interests.find((interest) => {
        return interest.uid === item.interests[0];
      });
      const category = i ? getSSRTranslation(i.name) : item.interests[0];
      return {
        uid: item.uid,
        title: item.title[i18n.language] || Object.values(item.title)[0],
        category: category,
        publisherLogo: item.brandLogo,
        publisherName: item.owner,
        darkPublisherLogo: item.brandLogoDark,
        isDark: theme === 'dark',
        image:
          item.previews?.find((p) => p.aspect === '1:1')?.link ||
          item.previews?.[0]?.link ||
          '',
        link: item.link,
        owner: item.owner,
      };
    });

  return (
    <div
      className="recirculation-page"
      data-testid="test-recirculation-page"
      role="list"
    >
      <div
        className="recirculation-page-scroll-content"
        onScroll={(event) => {
          if (onScroll) {
            onScroll(event.currentTarget.scrollTop !== 0);
          }
        }}
      >
        <h2 className="header-md">{t('recommendedForYou')}</h2>
        {isLoading
          ? Array.from({ length: 5 }).map((_, index) => (
              <RecirculationCardSkeleton key={index} />
            ))
          : cards
              .filter((card) => card.uid !== lookbookId)
              .map((card) => <RecirculationCard key={card.uid} {...card} />)}
      </div>
    </div>
  );
};

export default RecirculationPage;
